import { Injectable } from '@angular/core';
import { Sort } from '@angular/material/sort';
import { IBanks } from 'ClientApp/app/modules/join-form/models/banks/banks';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import { combineLatest } from 'rxjs/observable/combineLatest';
import { map, tap } from 'rxjs/operators';
import { HttpService, IResponseLower } from '../../../../../shared/cardholders-core/services/http-service.service';
import {
    HokHeaderItem,
    IGetHokDetailsForAllCardRequest,
    IGetHokDetailsForAllCardResponse,
    IGetHokDetailsForAllCardAndContentResponse,
    GetHokDetailsForCardResponse
} from '../../../interfaces/IGetHokDetailsForAllCard.interface';
import { DirectDebitService } from '../../../services/direct-debit.service';
import { EHokStatus, IFilterBank, IFilterCard, IFilterState, IFilterUsers } from '../interfaces/filters.interface';
import {
    ICancelDirectDebitRequest,
    ICancelDirectDebitResponse,
    ICheckCBKRequest,
    IIsClaimExistsResult
} from '../interfaces/cancel.interface';
import { DataHelper } from '../helpers/data.helper';

@Injectable()
export class DebitListService {
    hokDetailsForAllCard = new BehaviorSubject<IGetHokDetailsForAllCardResponse>(null);
    rc = -1;
    filterOpen$ = new BehaviorSubject<boolean>(false);
    state = new BehaviorSubject<IFilterState>({
        selectedCard: null,
        selectedUser: null,
        selectedStatus: EHokStatus.Active,
        selectedSort: { active: 'lastDebitDate', direction: 'asc' }
    });
    constructor(private httpService: HttpService, private directDebSrv: DirectDebitService) {}

    getHokDetailsForAllCard(
        req: IGetHokDetailsForAllCardRequest
    ): Observable<IResponseLower<IGetHokDetailsForAllCardAndContentResponse>> {
        return this.httpService
            .postLower<IGetHokDetailsForAllCardAndContentResponse>(
                `${this.directDebSrv.baseApi}/getHokDetailsForAllCard`,
                req
            )
            .pipe(
                tap(x => {
                    this.rc = x.returnCode;
                    if (x.result) {
                        this.hokDetailsForAllCard.next(x.result.getHokDetailsForAllCard);
                        this.directDebSrv.loader = false;
                    }
                })
            );
    }
    cancelDirectDebit(req: ICancelDirectDebitRequest): Observable<IResponseLower<ICancelDirectDebitResponse>> {
        return this.httpService
            .postLower<ICancelDirectDebitResponse>(`${this.directDebSrv.baseApi}/cancelDirectDebit`, req)
            .pipe(
                tap(x => {
                    this.rc = x.returnCode;
                })
            );
    }
    checkCBK(req: ICheckCBKRequest): Observable<IResponseLower<IIsClaimExistsResult>> {
        return this.httpService.postLower<IIsClaimExistsResult>(`${this.directDebSrv.baseApi}/checkCBK`, req);
    }

    getAllUsers(): Observable<IFilterUsers[]> {
        return this.hokDetailsForAllCard.pipe(
            map(x =>
                x?.usersHokDetails.map(user => {
                    return { name: user.user.name, id: user.user.id };
                })
            )
        );
    }
    getAllBankAndCard(): Observable<IFilterBank[]> {
        return combineLatest([this.hokDetailsForAllCard, this.state]).pipe(
            map(x => {
                const banks: IFilterBank[] = [];
                if (!x[0]) {
                    return [];
                }
                if (x[1].selectedUser) {
                    const currentUserIndex = x[0].usersHokDetails.findIndex(
                        users => users.user.id === x[1].selectedUser.id
                    );

                    x[0].usersHokDetails[currentUserIndex].cardsHokDetails.forEach((card, index) => {
                        const currentBank = banks.find(
                            bank =>
                                bank.accountNumber === card.bankDetails.accountNumber &&
                                bank.bankID === card.bankDetails.bankID &&
                                bank.branchID === card.bankDetails.branchID
                        );
                        if (!currentBank) {
                            banks.push({
                                bankID: card.bankDetails.bankID,
                                branchID: card.bankDetails.branchID,
                                bankName: card.bankDetails.bankName,
                                accountNumber: card.bankDetails.accountNumber,
                                cards: [this.generateCard(card, currentUserIndex)],
                                bankAccountIndex: index
                            });
                        } else {
                            currentBank.cards.push(this.generateCard(card, currentUserIndex));
                        }
                    });
                } else {
                    const cards: IFilterCard[] = [];
                    x[0].usersHokDetails.forEach((user, userIndex) => {
                        user.cardsHokDetails.forEach((card, index) => {
                            const currentBank = banks.find(
                                bank =>
                                    bank.accountNumber === card.bankDetails.accountNumber &&
                                    bank.bankID === card.bankDetails.bankID &&
                                    bank.branchID === card.bankDetails.branchID
                            );
                            if (!currentBank) {
                                banks.push({
                                    bankID: card.bankDetails.bankID,
                                    branchID: card.bankDetails.branchID,
                                    bankName: card.bankDetails.bankName,
                                    accountNumber: card.bankDetails.accountNumber,
                                    cards: [this.generateCard(card, userIndex)],
                                    bankAccountIndex: index
                                });
                            } else {
                                currentBank.cards.push(this.generateCard(card, userIndex));
                            }
                        });
                    });
                }
                return banks;
            })
        );
    }
    generateCard(card: GetHokDetailsForCardResponse, currentUserIndex: number): IFilterCard {
        return {
            shortCardNumber: card.shortCardNumber,
            cardIndex: card.cardIndex,
            cardImage: card.cardImage,
            activeStatus: card.activeStatus,
            cardName: card.cardName,
            userIndex: currentUserIndex
        };
    }
    getAllHokByState(): Observable<HokHeaderItem[]> {
        return combineLatest([this.hokDetailsForAllCard, this.state])
            .pipe(
                map(x => {
                    const hoks: HokHeaderItem[] = [];
                    let foundCard;
                    if (!x[0]) {
                        return [];
                    }
                    x[0].usersHokDetails.forEach(user => {
                        if (x[1].selectedUser) {
                            if (user.user.id !== x[1].selectedUser.id) {
                                return;
                            }
                        }
                        user.cardsHokDetails.forEach(card => {
                            if (x[1].selectedCard) {
                                foundCard = x[1].selectedCard.find(scard => scard.cardIndex === card.cardIndex);
                                if (!foundCard) {
                                    return;
                                }
                            }
                            card.hokDetails.forEach(hok => {
                                hok.cardIndex = card.cardIndex;
                                switch (x[1].selectedStatus) {
                                    case EHokStatus.Active:
                                        if (hok.status === 1) {
                                            hoks.push(hok);
                                        }

                                        break;
                                    case EHokStatus.Canceled:
                                        if (hok.status === 2) {
                                            hoks.push(hok);
                                        }

                                        break;
                                    case EHokStatus.Off:
                                        if (hok.status === 0) {
                                            hoks.push(hok);
                                        }

                                        break;
                                    case EHokStatus.Transference:
                                        if (hok.messageStatus === '1' || hok.messageStatus === '2') {
                                            hoks.push(hok);
                                        }
                                        break;
                                    case EHokStatus.All:
                                    default:
                                        hoks.push(hok);
                                        break;
                                }
                            });
                        });
                    });
                    return hoks;
                })
            )
            .pipe(
                map(x => {
                    return this.onSort(x);
                })
            );
    }
    onSort(data: HokHeaderItem[]): HokHeaderItem[] {
        const event = this.state.getValue().selectedSort;
        if (!event) {
            return data;
        }
        switch (event.active) {
            case 'merchantName':
                return data.sort((a, b) => {
                    return DataHelper.SortString(a.merchantName, b.merchantName, event.direction);
                });

            case 'sourceCard': {
                return data.sort((a, b) => {
                    return DataHelper.sortNumber(a.sourceCard, b.sourceCard, event.direction);
                });
            }

            case 'firstPurchaseDate': {
                return data.sort((a, b) => {
                    return DataHelper.sortDate(a.firstPurchaseDate, b.firstPurchaseDate, event.direction);
                });
            }
            case 'lastDebitDate': {
                return data.sort((a, b) => {
                    return DataHelper.sortDate(a.lastDebitDate, b.lastDebitDate, event.direction);
                });
            }
            case 'amount': {
                return data.sort((a, b) => {
                    return DataHelper.sortNumber(a.amount, b.amount, event.direction);
                });
            }
            case 'status':
                return data.sort((a, b) => {
                    return DataHelper.SortString(
                        this.getStatusName(this.getStatusEnum(a)),
                        this.getStatusName(this.getStatusEnum(b)),
                        event.direction
                    );
                });

            default:
                return data;
        }
    }
    getStatusEnum(hok: HokHeaderItem): EHokStatus {
        if (hok.messageStatus === '1' || hok.messageStatus === '2') {
            return EHokStatus.Transference;
        }
        if (hok.status === 2) {
            return EHokStatus.Canceled;
        }
        if (hok.status === 1) {
            return EHokStatus.Active;
        }
        if (hok.status === 0) {
            return EHokStatus.Off;
        }
    }
    getStatusName(status: EHokStatus): string {
        switch (status) {
            case EHokStatus.Active:
                return 'פעיל';
            case EHokStatus.All:
                return 'כל ההוראות';
            case EHokStatus.Canceled:
                return 'בוטל';
            case EHokStatus.Off:
                return 'לא פעיל';
            case EHokStatus.Transference:
                return 'העברה';

            default:
                return '';
        }
    }
}
